<template>
  <el-popover
    v-if="enabled"
    ref="popoverRef"
    placement="bottom-end"
    :width="400"
    trigger="click"
    popper-class="notif-popover"
    @show="onOpen"
  >
    <template #reference>
      <el-badge
        v-bind="$attrs"
        :value="unreadCount"
        :hidden="unreadCount === 0"
        :max="99"
        class="notif-badge"
      >
        <el-button
          class="right-menu-item hover-effect fullscreen-btn"
          circle
        >
          <Icon
            icon="mdi:bell-outline"
            :width="22"
          />
        </el-button>
      </el-badge>
    </template>

    <div class="notif-panel">
      <div class="notif-header">
        <span class="notif-title">Notifications</span>
        <el-button
          v-if="unreadCount > 0"
          text
          size="small"
          @click="onMarkAll"
        >
          Tout marquer lu
        </el-button>
      </div>

      <div
        v-loading="loading"
        class="notif-list"
      >
        <template v-if="items.length">
          <NotificationItem
            v-for="n in items"
            :key="n.id"
            :notification="n"
            dense
            @open="onClick"
            @toggle-read="onToggleRead"
          />
        </template>
        <div
          v-else
          class="notif-empty"
        >
          Aucune notification
        </div>
      </div>

      <div class="notif-footer">
        <el-button
          text
          size="small"
          @click="goPreferences"
        >
          Préférences
        </el-button>
        <el-button
          text
          size="small"
          @click="ouvrirLaListe"
        >
          Voir toutes les notifications
        </el-button>
      </div>
    </div>
  </el-popover>

  <!-- Hors du popover : son contenu est démonté à la fermeture, et le clic qui
       ouvre la fenêtre ferme justement le popover. -->
  <el-dialog
    v-if="enabled"
    v-model="listeOuverte"
    title="Notifications"
    width="900px"
    class="notif-dialog"
    destroy-on-close
  >
    <NotificationsBrowser
      compact
      @navigate="listeOuverte = false"
    />

    <template #footer>
      <el-button
        text
        @click="allerAuCentre"
      >
        Ouvrir en pleine page
      </el-button>
      <el-button @click="listeOuverte = false">
        Fermer
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { useNotifications } from '@/composables/useNotifications';
import NotificationItem from '@/components/Notifications/NotificationItem.vue';
import NotificationsBrowser from '@/components/Notifications/NotificationsBrowser.vue';

// Deux racines depuis l'ajout de la fenêtre : la classe posée par la barre de
// navigation doit continuer d'atterrir sur la cloche, pas se perdre.
defineOptions({ inheritAttrs: false });

const store = useStore();
const router = useRouter();
const { enabled, unreadCount, fetchList, markRead, markUnread, markAllRead } = useNotifications();

// Le popover reste NON contrôlé : renseigner `visible` désactiverait ses
// déclencheurs (Element Plus ignore les triggers en mode contrôlé). On le ferme
// donc par sa méthode exposée.
const popoverRef = ref(null);
const listeOuverte = ref(false);
const items = computed(() => store.state.notifications.items);
const loading = computed(() => store.state.notifications.loading);

const onOpen = () => fetchList({ per_page: 15 }).catch(() => {});
const onMarkAll = () => markAllRead().catch(() => {});

const onClick = async (n) => {
    // Marquer lu est best-effort : ne doit JAMAIS bloquer la navigation.
    if (!n.read_at) {
        try { await markRead(n.id); } catch (e) { /* ignoré volontairement */ }
    }
    const url = n.data?.action_url || n.data?.url;
    if (url) {
        popoverRef.value?.hide();
        router.push(url);
    }
};

// Bascule lu / non lu sans quitter la liste : ce qu'on ouvre sans pouvoir le
// traiter doit pouvoir retourner dans la pile.
const onToggleRead = async (n) => {
    try {
        await (n.read_at ? markUnread(n.id) : markRead(n.id));
    } catch (e) { /* l'état affiché reste celui du serveur */ }
};

const fermerEtAller = (chemin) => {
    popoverRef.value?.hide();
    router.push(chemin);
};

const goPreferences = () => fermerEtAller('/profile/notifications');

// La liste complète sans quitter l'écran courant : lues, non lues, filtrables.
const ouvrirLaListe = () => {
    popoverRef.value?.hide();
    listeOuverte.value = true;
};

const allerAuCentre = () => {
    listeOuverte.value = false;
    router.push('/notifications');
};
</script>

<style lang="scss" scoped>
.notif-badge {
    line-height: 1;

    /* Par défaut Element Plus pousse la pastille hors du bouton
       (translate(100%, -50%)) : dans une barre de 50 px elle sortait par le
       haut, rognée, et empiétait sur l'icône voisine. Le bouton de 32 px est
       centré dans la barre, donc neuf pixels la séparent du bord : la pastille
       en prend quatre, ce qui suffit à dégager le dôme de la cloche sans
       qu'elle soit rognée, et deux seulement à droite, où l'icône voisine
       n'est qu'à huit pixels. Elle s'allonge vers la gauche, « 99+ » compris. */
    :deep(.el-badge__content.is-fixed) {
        top: -4px;
        right: -2px;
        transform: none;
        height: 16px;
        line-height: 16px;
        padding: 0 4px;
        font-size: 11px;
        border: none;
    }
}
</style>

<style lang="scss">
/* Non scopé : le contenu du popover est téléporté hors du composant.
   Couleurs via variables Element Plus → adaptées light/dark automatiquement. */
.notif-popover.el-popover {
    padding: 0;
}

.notif-panel {
    display: flex;
    flex-direction: column;
    max-height: 460px;
}

.notif-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid var(--el-border-color-lighter);

    .notif-title {
        font-weight: 600;
        color: var(--el-text-color-primary);
    }
}

.notif-list {
    overflow-y: auto;
    min-height: 60px;
}

.notif-empty {
    padding: 24px 14px;
    text-align: center;
    color: var(--el-text-color-secondary);
    font-size: 13px;
}

.notif-footer {
    display: flex;
    justify-content: space-between;
    padding: 6px 8px;
    border-top: 1px solid var(--el-border-color-lighter);
}

/* Posée sur le coin de la cloche, la pastille touche les traits du dessin. Un
   liseré de la couleur de la barre l'en détache — d'où le couplage assumé avec
   les deux fonds de `NavbarView` plutôt qu'une variable Element Plus, dont le
   fond sombre (#141414) n'est pas celui de la barre. */
.navbar .notif-badge .el-badge__content.is-fixed {
    box-shadow: 0 0 0 1px #ffffff;
}

.navbar--dark .notif-badge .el-badge__content.is-fixed {
    box-shadow: 0 0 0 1px #02264a;
}

/* La fenêtre est téléportée dans le body : un style scopé ne l'atteindrait pas.
   La classe posée sur <el-dialog> atterrit sur l'enveloppe, d'où le sélecteur
   descendant — c'est déjà la forme retenue pour la fenêtre des notes de version.
   La marge verticale est écrite ici parce que `element-ui.scss` impose
   `.el-dialog { margin: 0 auto }`, qui annule la prop `top`. */
.notif-dialog .el-dialog {
    max-width: calc(100vw - 32px);
    margin: 6vh auto;
}

.notif-dialog .el-dialog__body {
    padding-top: 12px;
}
</style>
